import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Tab } from "./components/types";

type StoredTab = {
  path: string;
  hidden?: boolean;
};

export const useLocalStorageTabs = (
  initialTabs: Tab[],
  enableEditing: boolean,
  persistTabs: boolean,
  defaultPath: string,
  editableLocationKey?: string,
) => {
  /* --------------------------------
     URL (ONLY IF persistTabs = true)
  -------------------------------- */
  const [searchParams, setSearchParams] = useSearchParams();

  /* --------------------------------
     TABS STATE (localStorage SAME AS BEFORE)
  -------------------------------- */
  const [tabs, setTabs] = useState<Tab[]>(
    enableEditing
      ? loadTabsFromStorage(initialTabs, editableLocationKey ?? "tab-order")
      : initialTabs,
  );

  const visibleTabs = useMemo(() => tabs.filter((tab) => !tab.hidden), [tabs]);

  /* --------------------------------
     SELECTED TAB STATE
  -------------------------------- */

  // 🔹 Internal state (used when persistTabs = false)
  const [internalIndex, setInternalIndex] = useState(0);

  // 🔹 URL-based selected tab (used when persistTabs = true)
  const selectedIndexFromUrl = useMemo(() => {
    if (!persistTabs) return null;

    const tabParam = searchParams.get("tab") ?? defaultPath;
    const index = visibleTabs.findIndex((tab) => tab.path === tabParam);

    return index === -1 ? 0 : index;
  }, [persistTabs, searchParams, visibleTabs, defaultPath]);

  const currentSelectedTabIndex =
    persistTabs && selectedIndexFromUrl !== null
      ? selectedIndexFromUrl
      : internalIndex;

  /* --------------------------------
     TAB CHANGE HANDLER
  -------------------------------- */
  const changeTabWithUrl = (index: number) => {
    setSearchParams((params) => {
      params.set("tab", visibleTabs[index].path);
      return params;
    });
  };

  const changeTabInMemory = (index: number) => {
    setInternalIndex(index);
  };

  const changeSelectedTab = persistTabs ? changeTabWithUrl : changeTabInMemory;

  /* --------------------------------
     UPDATE TABS (ORDER / HIDDEN)
     localStorage behavior UNCHANGED
  -------------------------------- */
  const updateTabs = (newTabs: Tab[]) => {
    const cleanedTabs = newTabs.map((t) => ({
      path: t.path,
      hidden: t.hidden,
    }));

    localStorage.setItem(
      (editableLocationKey ?? "tab-order") + ".tabOrder",
      JSON.stringify(cleanedTabs),
    );

    setTabs(newTabs);

    // Safety: if current tab becomes hidden in memory mode
    if (!persistTabs) {
      setInternalIndex(0);
    }
  };

  return {
    allTabs: tabs,
    visibleTabs,
    currentSelectedTabIndex,
    changeSelectedTab,
    updateTabs,
  };
};

/* --------------------------------
   LOCAL STORAGE HELPERS
-------------------------------- */
const loadTabsFromStorage = (tabs: Tab[], key: string): Tab[] => {
  const storedTabs = localStorage.getItem(key + ".tabOrder");

  if (!storedTabs) {
    return tabs;
  }

  try {
    const parsedTabs: StoredTab[] = JSON.parse(storedTabs);

    const restoredTabs = parsedTabs.reduce<Tab[]>((acc, storedTab) => {
      const originalTab = tabs.find((tab) => tab.path === storedTab.path);
      if (!originalTab) return acc;

      acc.push({
        ...originalTab,
        hidden: storedTab.hidden,
      });

      return acc;
    }, []);

    const missingTabs = tabs.filter(
      (tab) => !restoredTabs.some((t) => t.path === tab.path),
    );

    return [...restoredTabs, ...missingTabs];
  } catch {
    return tabs;
  }
};
